import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, TemplateRef, computed, contentChild, input, output, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';

/** Direction of a side-zone navigation tap. */
export const STORY_TAP_DIRECTION = {
  PREVIOUS: 'previous',
  NEXT: 'next',
} as const;
export type StoryTapDirection = typeof STORY_TAP_DIRECTION[keyof typeof STORY_TAP_DIRECTION];

export interface StoryReelSlide {
  type?: string;
}

export interface StoryReelStory {
  slides?: readonly StoryReelSlide[];
}

export interface StorySlideContext {
  $implicit: StoryReelSlide;
  story: StoryReelStory;
  slideIndex: number;
  storyIndex: number;
  isFirstSlide: boolean;
  isLastSlide: boolean;
  isFirstStory: boolean;
  isLastStory: boolean;
}

type TapZone = 'prev' | 'next' | 'middle';

@Component({
  selector: 'app-story-viewer',
  templateUrl: './story-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  styles: [`
    .story-timed-fill {
      width: 0;
      animation-name: storyFill;
      animation-timing-function: linear;
      animation-fill-mode: forwards;
    }
    @keyframes storyFill { from { width: 0; } to { width: 100%; } }
  `],
  animations: [
    trigger('storySwitch', [
      transition(':increment', [
        style({ transform: 'translateX(100%)' }),
        animate('250ms ease-out', style({ transform: 'translateX(0)' })),
      ]),
      transition(':decrement', [
        style({ transform: 'translateX(-100%)' }),
        animate('250ms ease-out', style({ transform: 'translateX(0)' })),
      ]),
    ]),
  ],
})
export class StoryViewerComponent implements OnInit, OnDestroy {
  private static readonly HOLD_THRESHOLD_MS = 200;
  readonly stories = input.required<readonly StoryReelStory[]>();
  readonly startStoryIndex = input(0);
  readonly startSlideIndex = input(0);
  readonly videoType = input('VIDEO');
  readonly autoAdvanceMs = input(10_000);

  readonly closed = output<void>();
  readonly storyChange = output<number>();
  readonly tapNavigate = output<{ direction: StoryTapDirection; story: StoryReelStory; storyIndex: number }>();
  readonly completed = output<void>();

  protected readonly bodyTemplate = contentChild.required<TemplateRef<StorySlideContext>>('storySlide');
  protected readonly footerTemplate = contentChild<TemplateRef<StorySlideContext>>('storyFooter');

  protected readonly storyIndex = signal(0);
  protected readonly slideIndex = signal(0);
  protected readonly videoProgress = signal(0);
  readonly paused = signal(false);

  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private isHolding = false;

  protected readonly currentSlides = computed<readonly StoryReelSlide[]>((): readonly StoryReelSlide[] => this.stories()[this.storyIndex()]?.slides ?? []);
  protected readonly segments = computed<number[]>((): number[] => this.currentSlides().map((_: StoryReelSlide, i: number): number => i));
  protected readonly currentSlideIsVideo = computed<boolean>((): boolean => this.currentSlides()[this.slideIndex()]?.type === this.videoType());
  protected readonly context = computed<StorySlideContext>((): StorySlideContext => {
    const storyIndex = this.storyIndex();
    const slideIndex = this.slideIndex();
    const stories = this.stories();
    const story = stories[storyIndex];
    const slides: readonly StoryReelSlide[] = story?.slides ?? [];
    return {
      $implicit: slides[slideIndex],
      story,
      slideIndex,
      storyIndex,
      isFirstSlide: slideIndex === 0,
      isLastSlide: slideIndex >= slides.length - 1,
      isFirstStory: storyIndex === 0,
      isLastStory: storyIndex >= stories.length - 1,
    };
  });

  ngOnInit(): void {
    const lastStory = Math.max(this.stories().length - 1, 0);
    const storyIdx = Math.min(Math.max(this.startStoryIndex(), 0), lastStory);
    const lastSlide = Math.max((this.stories()[storyIdx]?.slides?.length ?? 1) - 1, 0);
    this.storyIndex.set(storyIdx);
    this.slideIndex.set(Math.min(Math.max(this.startSlideIndex(), 0), lastSlide));
    this.storyChange.emit(storyIdx);
  }

  ngOnDestroy(): void {
    this.clearHoldTimer();
  }

  notifyVideoProgress(event: { currentTime: number; duration: number }): void {
    this.videoProgress.set(event.duration > 0 ? Math.min(event.currentTime / event.duration, 1) : 0);
  }

  notifyVideoEnded(): void {
    this.next();
  }

  protected previous(): void {
    if (this.slideIndex() > 0) {
      this.setSlide(this.slideIndex() - 1);
      return;
    }
    if (this.storyIndex() > 0) {
      this.goToStory(this.storyIndex() - 1);
    }
  }

  protected next(): void {
    if (this.slideIndex() < this.currentSlides().length - 1) {
      this.setSlide(this.slideIndex() + 1);
      return;
    }
    if (this.storyIndex() < this.stories().length - 1) {
      this.goToStory(this.storyIndex() + 1);
      return;
    }
    this.completed.emit();
  }

  protected close(): void {
    this.closed.emit();
  }

  protected onPointerDown(): void {
    this.isHolding = false;
    this.clearHoldTimer();
    this.holdTimer = setTimeout((): void => {
      this.isHolding = true;
      this.paused.set(true);
    }, StoryViewerComponent.HOLD_THRESHOLD_MS);
  }

  protected onPointerUp(zone: TapZone): void {
    this.clearHoldTimer();
    if (this.isHolding) {
      this.isHolding = false;
      this.paused.set(false);
      return;
    }
    if (zone === 'prev') {
      this.tapNavigate.emit({ direction: STORY_TAP_DIRECTION.PREVIOUS, story: this.stories()[this.storyIndex()], storyIndex: this.storyIndex() });
      this.previous();
    } else if (zone === 'next') {
      this.tapNavigate.emit({ direction: STORY_TAP_DIRECTION.NEXT, story: this.stories()[this.storyIndex()], storyIndex: this.storyIndex() });
      this.next();
    } else if (this.currentSlideIsVideo()) {
      this.paused.update((value: boolean): boolean => !value);
    }
  }

  protected onPointerCancel(): void {
    this.clearHoldTimer();
    if (this.isHolding) {
      this.isHolding = false;
      this.paused.set(false);
    }
  }

  private clearHoldTimer(): void {
    if (this.holdTimer !== null) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  private setSlide(index: number): void {
    this.videoProgress.set(0);
    this.paused.set(false);
    this.slideIndex.set(index);
  }

  private goToStory(index: number): void {
    this.videoProgress.set(0);
    this.paused.set(false);
    this.storyIndex.set(index);
    this.slideIndex.set(0);
    this.storyChange.emit(index);
  }
}
